import React from "react";
import type { SectionOptions } from "./types";
import { Shell, type SectionProps } from "./TemplateSections";
import { useCreatorData, useTemplateSectionInstance, TemplateSectionInstanceCtx } from "./templateData";
import {
  useCreatorLooks,
  useShopLinkIndividualPosts,
  normalizeLooksSlotCount,
  normalizeLooksTagsBeside,
  productBrandLabel,
  type CreatorLook,
  type LooksTagsBeside,
} from "./contentLooks";
import { merchCfgString, merchCfgStringList } from "./shopPageMerch";
import type { ShopCatalogProduct } from "./templateShopCatalog";
import { useCustomDomain } from "../../../hooks/useCustomDomain";
import type { DraftPreview } from "./websiteEditorInspectorTypes";
import { NoMediaPost } from "../LandingPageCarousels/CommonComponents/NoMediaPost";
import { CreatorFeedVideoPlayer } from "../../../components/FeedPost/PlatformPosts/CreatorFeedVideoPlayer";
import { FeedMediaPriorityProvider } from "../../../Utils/media/feedMediaPriority";
import { useEdit, E } from "./InlineEdit";
import {
  addHttpToLink,
  openOutboundUrl,
} from "../../../Utils/StayUpToDate/RenderComponent";
import { useLooksBandCatalogGate } from "./ShopInlineFeaturedContext";
import { useSectionNetworkCascade } from "./templateSectionNetworkCascade";
import { useTemplateAnalyticsOptional } from "./TemplateAnalyticsContext";
import { useWebsiteEditorInspectorOptional } from "./WebsiteEditorInspectorContext";
import { scrollElementToFeedTop } from "../../../Utils/feedScrollRoot";
import { TEMPLATE_ANALYTICS_EVENTS } from "../../../Utils/SupabaseServices/templateAnalyticsEvents";
import "../../../components/CreatorFeed/CreatorFeed.css";

/**
 * Recent Looks — FeaturedContent.jsx content-band (Hero→Quad) + ContentDrawer.
 * Featured Outfit — sticky look with scrollable products (no drawer).
 */

type LookProductDraft = {
  productId?: string;
  src?: string;
  zoom?: number;
  x?: number;
  y?: number;
};
type LookSlotDraft = {
  contentSource?: "content" | "upload";
  contentSrc?: string;
  contentZoom?: number;
  contentX?: number;
  contentY?: number;
  products?: LookProductDraft[];
};

function readLookSlotDrafts(cfg: SectionProps["cfg"], len: number): LookSlotDraft[] {
  const raw = cfg?.lookSlotDrafts;
  let arr: LookSlotDraft[] = [];
  if (Array.isArray(raw)) {
    arr = raw.map((v) =>
      v && typeof v === "object" ? (v as LookSlotDraft) : {},
    );
  }
  const next = arr.slice(0, len);
  while (next.length < len) next.push({});
  return next;
}

function lookTitle(caption: string, fallback: string) {
  const text = String(caption || "").replace(/\s+/g, " ").trim();
  if (!text) return fallback;
  return text.length > 72 ? `${text.slice(0, 69).trim()}…` : text;
}

function noMediaLabel(platform: string, caption: string): string {
  const p = platform.trim().toLowerCase();
  if (p === "newsletter") return caption || "Newsletter";
  if (p === "linkedin") return "LinkedIn Post";
  if (p === "twitter" || p === "x") return "X Post";
  return caption || "Post";
}

function lookProductLabel(raw: string): string {
  const text = String(raw || "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  const lower = text.toLowerCase();
  if (lower === "post" || lower === "product" || lower === "untitled") return "";
  // Collection rows sometimes store the shop URL as the title — never show it.
  if (/^https?:\/\//i.test(text) || /^www\./i.test(text)) return "";
  return text.length > 56 ? `${text.slice(0, 53).trim()}…` : text;
}

/**
 * Image that sits on the tile’s soft background. No opacity dance — the frame
 * already reserves space, so paint-in stays calm instead of jumpy.
 */
function LookTileImage({
  src,
  alt = "",
  className,
  style,
  priority = false,
}: {
  src: string;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  /** Above-the-fold hero look only — everything else stays lazy. */
  priority?: boolean;
}) {
  const imgRef = React.useRef<HTMLImageElement>(null);
  const [painted, setPainted] = React.useState(false);

  React.useLayoutEffect(() => {
    setPainted(false);
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) setPainted(true);
  }, [src]);

  return (
    <span className={`mte-tpl-look-img-rise${painted ? " is-in" : ""}`}>
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={className}
        style={style}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        onLoad={() => setPainted(true)}
        {...(priority ? ({ fetchPriority: "high" } as React.ImgHTMLAttributes<HTMLImageElement>) : null)}
      />
    </span>
  );
}

/** Rail of empty product tiles — shown while Featured Outfit collection posts resolve. */
function LookProductRailSkeleton({ count = 4 }: { count?: number }) {
  const n = Math.max(1, Math.min(8, count));
  return (
    <div
      className="mte-tpl-look-products is-scroll mte-tpl-look-products--skel"
      aria-busy="true"
      aria-label="Loading posts"
    >
      {Array.from({ length: n }, (_, i) => (
        <div
          key={`look-prod-skel-${i}`}
          className="mte-tpl-look-product mte-tpl-look-product--lib mte-tpl-look-product--skel"
        >
          <div className="mte-tpl-look-product-media" />
        </div>
      ))}
    </div>
  );
}

function lookProductHref(product: ShopCatalogProduct): string {
  // Prefer shop / affiliate URL; fall back to permalink so content posts in
  // the drawer / side tags remain clickable like products.
  const shop = product.url && product.url !== "#" ? String(product.url).trim() : "";
  const permalink = String(product.permalink || "").trim();
  return addHttpToLink(shop || permalink);
}

function LookProductTile({ product }: { product: ShopCatalogProduct }) {
  const templateAnalytics = useTemplateAnalyticsOptional();
  // Dashboard Preview mirrors the public site — keep product tiles clickable
  // even while the canvas is in edit mode (same as side-tag products).
  const shopHref = lookProductHref(product);
  const canShop = Boolean(shopHref);
  const platform = String(product.platform || "").trim().toLowerCase();
  const videoSrc = String(product.mediaUrl || "").trim();
  const playVideo = Boolean(product.isVideo && videoSrc);
  const thumb = String(product.image || "").trim() || "/assets/dummy_video.png";
  const isText =
    Boolean(product.isText) ||
    (!product.image &&
      !playVideo &&
      (platform === "linkedin" ||
        platform === "twitter" ||
        platform === "x" ||
        platform === "newsletter"));
  const name = lookProductLabel(product.name);
  const brand = productBrandLabel(product);
  const shopPill = canShop ? (
    <span className="mte-tpl-look-product-shop" aria-hidden>
      Shop
    </span>
  ) : null;

  const media = isText ? (
    <div className="mte-tpl-look-product-media">
      <NoMediaPost
        post={{
          platform: product.platform,
          caption: product.name,
          post_date: product.postDate,
          post_id: product.postId,
        }}
        type={platform || "content"}
        label={noMediaLabel(platform, product.name)}
        className="newsletter-media-carousel creator-feed-card-links-image no-media-url mte-tpl-look-nomedia-card"
        showDate={Boolean(product.postDate)}
        showMeta={false}
      />
      {shopPill}
    </div>
  ) : (
    <div className="mte-tpl-look-product-media">
      {playVideo ? (
        <CreatorFeedVideoPlayer
          media_url={videoSrc}
          thumbnail={thumb}
          isSoundOn
          className="creator-feed-card-video hover-play mte-tpl-look-hover-video"
        />
      ) : product.image ? (
        <LookTileImage
          src={product.image}
          className="creator-feed-card-links-image"
        />
      ) : (
        <span className="mte-tpl-look-product-media-ph" aria-hidden />
      )}
      {shopPill}
    </div>
  );

  const caption = (
    <div className="mte-tpl-look-product-caption">
      {brand ? (
        <span className="mte-tpl-look-product-brand">{brand}</span>
      ) : null}
      {name || product.name ? (
        <span className="mte-tpl-look-product-name product-name">
          <E
            path={`product.${product.postId}.name`}
            className="mte-tpl-prod-name-edit"
          >
            {name || product.name}
          </E>
        </span>
      ) : null}
    </div>
  );

  const className = `mte-tpl-look-product mte-tpl-look-product--lib${
    isText ? " mte-tpl-look-product--nomedia" : ""
  }`;

  if (canShop) {
    return (
      <a
        href={shopHref}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          const productLabel = String(name || product.name || "Shop").trim() || "Shop";
          templateAnalytics?.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.SECTION_CLICK, {
            sectionId: "featuredOutfit",
            sectionType: "Featured Outfit",
            entityType: "section",
            entityId: String(product.postId || productLabel),
            entityLabel: productLabel,
            details: {
              name: productLabel,
              image: String(product.image || "").trim(),
              post_id: String(product.postId || ""),
            },
          });
        }}
      >
        {media}
        {caption}
      </a>
    );
  }

  return (
    <div className={className}>
      {media}
      {caption}
    </div>
  );
}

function LookProductRow({
  products,
  scrollable,
  style,
}: {
  products: ShopCatalogProduct[];
  scrollable?: boolean;
  /** `lib` = LiB tiles; `drawer` = mockup hairline/shot/brand/name cells. */
  style?: "lib" | "drawer";
}) {
  if (!products.length) {
    return (
      <p className="mte-tpl-merch-empty">No products in this collection yet.</p>
    );
  }
  if (style === "drawer") {
    return (
      <div
        className={`mte-tpl-look-products mte-fc-drawer-row${
          scrollable ? " is-scroll" : ""
        }`}
      >
        {products.slice(0, 24).map((product, i) => (
          <DrawerProductCell
            key={`${product.postId || "prod"}-${i}`}
            product={product}
            compact
          />
        ))}
      </div>
    );
  }
  return (
    <div className={`mte-tpl-look-products${scrollable ? " is-scroll" : ""}`}>
      {products.slice(0, 24).map((product, i) => (
        <LookProductTile
          key={`${product.postId || "prod"}-${i}`}
          product={product}
        />
      ))}
    </div>
  );
}

// ── Featured Outfit ─────────────────────────────────────────────

function useFeaturedOutfitModel(
  cfg?: SectionProps["cfg"],
  /** False while the content page layer is CSS-hidden (pre-mounted). */
  enabled = true,
) {
  const CREATOR = useCreatorData();
  const { customDomain } = useCustomDomain();
  const username = (CREATOR.bio?.username || "").trim().toLowerCase();
  const userId = CREATOR.bio?.userId || "";
  const postId = merchCfgString(cfg, "postId");
  const focusPostIds = React.useMemo(
    () => (postId ? [postId] : null),
    [postId],
  );
  const { looks, loading, firstPageReady } = useCreatorLooks({
    username,
    userId,
    enabled: Boolean(enabled && username && userId),
    customDomain,
    // Page 1 first; background-warm pages 2–3 after settle (75 look pool).
    maxPages: 3,
    autoWarmPages: true,
    focusPostIds,
  });
  // Gate Content catalog until this outfit can paint (page 1 + focus catch-up).
  useLooksBandCatalogGate(Boolean(enabled && firstPageReady && !loading));
  const cfgImage = merchCfgString(cfg, "postImage");
  const cfgCaption = merchCfgString(cfg, "postCaption");
  const look =
    (postId && looks.find((item) => item.post.postId === postId)) || null;
  const heading = merchCfgString(cfg, "heading");
  const shopUrl = look?.post.shopUrl || "";

  const shopPosts = useShopLinkIndividualPosts({
    shopUrl,
    postId,
    username,
    userId,
    enabled: Boolean(enabled && (postId || shopUrl)),
  });

  const products =
    shopPosts.products.length > 0
      ? shopPosts.products
      : look?.products || [];

  const hasPost = Boolean(postId);
  const image = look?.post.image || cfgImage || "";
  const title = look
    ? lookTitle(
        look.post.caption,
        shopPosts.component?.name ||
          look.collection?.name ||
          "Featured look",
      )
    : lookTitle(cfgCaption, heading);

  return {
    heading,
    hasPost,
    look,
    image,
    title,
    products,
    productsLoading: shopPosts.loading,
    looksLoading: loading,
    firstPageReady,
    shopUrl: shopPosts.shopUrl || shopUrl,
    shopSlug: shopPosts.slug,
    shopComponent: shopPosts.component,
  };
}

function FeaturedOutfitBody({
  isMobile,
  model,
  cfg,
}: {
  isMobile?: boolean;
  model: ReturnType<typeof useFeaturedOutfitModel>;
  cfg?: SectionProps["cfg"];
}) {
  const {
    heading,
    hasPost,
    look,
    image,
    products,
    productsLoading,
    looksLoading,
    shopUrl,
    shopSlug,
    shopComponent,
  } = model;

  const sectionPath = useTemplateSectionInstance();
  const headingPath = sectionPath
    ? `section.${sectionPath}.heading`
    : "";
  const { get, set } = useEdit();
  // Prefer the section cfg heading written at Add. Only fall back to the
  // legacy shared path when this section has no title yet — otherwise a prior
  // edit of looks.featured.heading overwrites a newly created outfit's title.
  // Seed once per section path — re-running on every `cfg` change rewrote
  // contentEdits after Discard restore and fought the session snapshot.
  const seededHeadingPathRef = React.useRef("");
  React.useEffect(() => {
    if (!headingPath) return;
    if (seededHeadingPathRef.current === headingPath) return;
    if (get(headingPath, "\u0000") !== "\u0000") {
      seededHeadingPathRef.current = headingPath;
      return;
    }
    const cfgHeading = merchCfgString(cfg, "heading");
    if (cfgHeading) {
      set(headingPath, cfgHeading);
      seededHeadingPathRef.current = headingPath;
      return;
    }
    const legacy = get("looks.featured.heading", "\u0000");
    if (legacy === "\u0000") return;
    set(headingPath, legacy);
    seededHeadingPathRef.current = headingPath;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed once per path
  }, [headingPath]);

  const lookVideoSrc = String(look?.post.mediaUrl || "").trim();
  const lookPlayVideo = Boolean(look?.post.isVideo && lookVideoSrc);
  // First thumbnail wins. A later look payload often replaces it with a resized
  // URL, which unmounts the painted image and replays the pin fade.
  const pinnedThumbKeyRef = React.useRef("");
  const pinnedThumbRef = React.useRef("");
  const thumbKey = String(look?.post.postId || (hasPost ? "cfg" : ""));
  if (pinnedThumbKeyRef.current !== thumbKey) {
    pinnedThumbKeyRef.current = thumbKey;
    pinnedThumbRef.current = "";
  }
  const nextThumb = String(image || "").trim();
  if (nextThumb && !pinnedThumbRef.current) pinnedThumbRef.current = nextThumb;
  const lookThumb = pinnedThumbRef.current || nextThumb || "/assets/dummy_video.png";

  const resolveHint = !shopUrl
    ? "This post needs a Browsable shop link to load collection posts."
    : !shopSlug
      ? "Couldn’t read a collection slug from the shop link."
      : !shopComponent
        ? `No collection found for “${shopSlug}”.`
        : "No posts in this collection yet.";

  // Latch: once products painted, don’t drop back to skeleton on refetch flicker.
  const paintedProductsRef = React.useRef(false);
  const lookKey = String(look?.post.postId || model.hasPost || "");
  React.useEffect(() => {
    paintedProductsRef.current = false;
  }, [lookKey]);
  if (products.length > 0) paintedProductsRef.current = true;

  // One continuous pending phase: looks warm → product pipeline → content.
  const resolvingLook = Boolean(hasPost && !look && looksLoading);
  const railPending =
    hasPost &&
    !paintedProductsRef.current &&
    (looksLoading ||
      resolvingLook ||
      (!products.length && productsLoading));
  const showEmptyHint =
    hasPost && !railPending && !products.length && !looksLoading && !productsLoading;

  return (
    <div className={`mte-tpl-looks mte-tpl-featured-outfit${isMobile ? " is-mobile" : ""}`}>
      <p className="mte-tpl-looks-kicker">
        {headingPath ? (
          <E path={headingPath} placeholder="Enter a title">
            {heading}
          </E>
        ) : (
          heading
        )}
      </p>
      {!hasPost ? (
        <p className="mte-tpl-merch-empty">
          Choose an Instagram post or reel with a Browsable shop link.
        </p>
      ) : (
        <div className="mte-tpl-featured-outfit-card">
          {/* Pinned look — content-page reel style: natural height, no crop. */}
          <div className="mte-tpl-featured-outfit-pin">
            <div
              className={`mte-tpl-featured-outfit-media${
                looksLoading && !image && !lookPlayVideo ? " is-ph" : ""
              }`}
            >
              {lookPlayVideo ? (
                <FeedMediaPriorityProvider priority>
                  {lookThumb !== "/assets/dummy_video.png" ? (
                    <img
                      src={lookThumb}
                      alt=""
                      className="mte-tpl-look-hover-video"
                      decoding="async"
                    />
                  ) : null}
                  <CreatorFeedVideoPlayer
                    media_url={lookVideoSrc}
                    thumbnail={lookThumb}
                    isSoundOn
                    className="creator-feed-card-video hover-play mte-tpl-look-hover-video"
                  />
                </FeedMediaPriorityProvider>
              ) : image ? (
                <LookTileImage src={lookThumb} priority />
              ) : looksLoading ? (
                <span className="mte-tpl-featured-outfit-media-ph" aria-hidden />
              ) : (
                <span className="mte-tpl-featured-outfit-media-ph" aria-hidden />
              )}
            </div>
          </div>

          {/* Scroll rail — products link out; no shop-the-look redirect. */}
          <div className="mte-tpl-featured-outfit-rail">
            {railPending ? (
              <LookProductRailSkeleton count={4} />
            ) : products.length ? (
              <LookProductRow products={products} scrollable style="lib" />
            ) : showEmptyHint ? (
              <p className="mte-tpl-merch-empty">{resolveHint}</p>
            ) : (
              <LookProductRailSkeleton count={4} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function FeaturedOutfitSection({
  T,
  cfg,
  isActive = true,
}: SectionProps) {
  const sectionPath = useTemplateSectionInstance();
  const cascade = useSectionNetworkCascade();
  const cascadeSectionId =
    String(sectionPath || "")
      .split("/")
      .pop()
      ?.trim() || "";
  const cascadeAllowsFetch =
    !cascade || !cascadeSectionId || cascade.canFetch(cascadeSectionId);
  const model = useFeaturedOutfitModel(
    cfg,
    Boolean(isActive && cascadeAllowsFetch),
  );

  React.useEffect(() => {
    if (!cascade || !cascadeSectionId || !isActive || !cascadeAllowsFetch) return;
    if (model.firstPageReady && !model.looksLoading) {
      cascade.reportSettled(cascadeSectionId);
    }
  }, [
    cascade,
    cascadeSectionId,
    isActive,
    cascadeAllowsFetch,
    model.firstPageReady,
    model.looksLoading,
  ]);

  const isMobile = T.device === "mobile";
  const shellCfg = {
    ...(cfg || {}),
    heading: undefined,
    bgTone:
      (cfg?.bgTone as "bg" | "surface" | "accent" | undefined) || "surface",
  };

  return (
    <Shell
      T={T}
      cfg={shellCfg}
      fullBleed
      independentShellClassName="mte-tpl-looks-shell"
    >
      <FeaturedOutfitBody isMobile={isMobile} model={model} cfg={cfg} />
    </Shell>
  );
}
FeaturedOutfitSection.options = {
  heading: {
    kind: "text",
    label: "Title",
    default: "",
    placeholder: "Enter a title",
  },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface"],
    default: "surface",
  },
} as SectionOptions;

export function ContentLooksGhostBody({ draft }: { draft: DraftPreview }) {
  const cfg = {
    ...(draft.cfg || {}),
    heading: draft.title || (draft.cfg?.heading as string) || undefined,
  };
  // Add ghost sits inside ContentGrid — without isolating instance ctx, seed +
  // <E> live-write section.content/grid.heading and Discard cannot cleanly revert.
  const body =
    draft.type === "recent-looks" ? (
      <RecentLooksSectionInner cfg={cfg} isMobile={false} />
    ) : (
      <FeaturedOutfitGhostBody cfg={cfg} />
    );
  return (
    <TemplateSectionInstanceCtx.Provider value={null}>
      {body}
    </TemplateSectionInstanceCtx.Provider>
  );
}

function FeaturedOutfitGhostBody({ cfg }: { cfg: SectionProps["cfg"] }) {
  const model = useFeaturedOutfitModel(cfg);
  return (
    <FeaturedOutfitBody
      model={model}
      cfg={cfg}
    />
  );
}

// ── Recent Looks = Chandler Featured Content ────────────────────

/** How many products are available to show beside a look (image or id). */
function countAvailableLookProducts(
  products: ShopCatalogProduct[],
  overrides: LookProductDraft[] | undefined,
): number {
  const fromLook = products.filter(
    (p) => String(p.image || "").trim() || String(p.postId || "").trim(),
  ).length;
  if (!overrides?.length) return fromLook;
  // Draft slots can point at uploads without a catalog row.
  const uploadExtras = overrides.filter(
    (o) => String(o.src || "").trim() && !String(o.productId || "").trim(),
  ).length;
  return Math.max(fromLook, uploadExtras);
}

/**
 * Creator override: Hero / Split up to 4 beside; Trio / Quad up to 3;
 * Defaults: Hero/Split 3, Trio 2, Quad None.
 * When products overflow the beside count, the last slot becomes “+N more”
 * (2→1 thumb+more, 3→2+more, 4→3+more).
 */
function tagWantForLayout(
  slotCount: number,
  tagsBeside: LooksTagsBeside = "auto",
): number {
  const layoutMax = slotCount <= 2 ? 4 : 3;
  if (tagsBeside !== "auto") {
    const want = Number(tagsBeside);
    if (want !== 2 && want !== 3 && want !== 4) return 0;
    return Math.min(want, layoutMax);
  }
  if (slotCount === 1 || slotCount === 2) return 3;
  if (slotCount >= 4) return 0; // Quad default: drawer only
  return 2;
}

type TagCell = {
  key: string;
  src: string;
  url: string;
  postId?: string;
  name?: string;
  zoom: number;
  x: number;
  y: number;
};

function resolveTagCells(
  products: ShopCatalogProduct[],
  overrides: LookProductDraft[] | undefined,
  want: number,
): TagCell[] {
  if (want <= 0) return [];
  const cells: TagCell[] = [];
  for (let i = 0; i < want; i += 1) {
    const ov = overrides?.[i];
    const p = products[i] || null;
    const byId = ov?.productId
      ? products.find((x) => x.postId === ov.productId) || null
      : null;
    // Prefer the draft productId match, but always fall back to the slot's
    // catalog row. When the same look is used in multiple Recent Looks slots,
    // drafts can briefly hold ids before shop posts resolve — without this
    // fallback the second entry loses a side-tag image.
    const picked = byId || p;
    const permalink = String(picked?.permalink || p?.permalink || "").trim();
    const rawUrl = String(picked?.url || p?.url || "").trim();
    const url =
      (rawUrl && rawUrl !== "#" ? rawUrl : "") || permalink || "#";
    const src = String(ov?.src || picked?.image || "").trim();
    const zoomRaw = Number(ov?.zoom);
    const xRaw = Number(ov?.x);
    const yRaw = Number(ov?.y);
    // Always emit a slot so the 2/3/4 footprint never collapses mid-load.
    cells.push({
      key: `tag-slot-${i}`,
      src,
      url,
      postId: picked?.postId || p?.postId,
      name: picked?.name || p?.name,
      zoom: zoomRaw > 0 ? zoomRaw : 1,
      x: xRaw >= 0 && xRaw <= 100 ? xRaw : 50,
      y: yRaw >= 0 && yRaw <= 100 ? yRaw : 50,
    });
  }
  return cells;
}

function tagLayoutClass(want: number): string {
  if (want >= 4) return "is-grid";
  if (want === 3) return "is-stack is-three";
  if (want === 2) return "is-stack";
  return "is-one";
}

function TagBlock({
  want,
  cells,
  moreCount,
  reserveMore,
  onMoreClick,
  onProductClick,
}: {
  /** Locked tile count from Products 2/3/4 — layout never depends on load state. */
  want: number;
  cells: TagCell[];
  moreCount?: number;
  /** Keep the last tile as a more-slot (label or soft fill) so footprint doesn’t swap. */
  reserveMore?: boolean;
  onMoreClick?: () => void;
  onProductClick?: (cell: TagCell) => void;
}) {
  if (want <= 0) return null;
  const more = Math.max(0, Number(moreCount) || 0);
  const showMoreTile = more > 0 || Boolean(reserveMore);
  // Image slots + optional more tile must equal `want` so layout stays fixed.
  const imageCount = showMoreTile ? Math.max(0, want - 1) : want;
  const slots = cells.slice(0, imageCount);
  while (slots.length < imageCount) {
    slots.push({
      key: `tag-slot-${slots.length}`,
      src: "",
      url: "#",
      zoom: 1,
      x: 50,
      y: 50,
    });
  }

  return (
    <div
      className={`mte-fc-tags ${tagLayoutClass(want)}`}
      aria-label="Products in this look"
    >
      {slots.map((c) => {
        const href = addHttpToLink(
          c.url && c.url !== "#" ? String(c.url).trim() : "",
        );
        // Allow opening even when the tag image is still resolving — a valid
        // shop/permalink URL should still be clickable (matches drawer cells).
        const canOpen = Boolean(href);
        return (
          <a
            key={c.key}
            href={canOpen ? href : undefined}
            className={`mte-fc-tag${c.src ? "" : " is-ph"}`}
            target={canOpen ? "_blank" : undefined}
            rel={canOpen ? "noopener noreferrer" : undefined}
            aria-hidden={c.src || canOpen ? undefined : true}
            onClick={(e) => {
              e.stopPropagation();
              if (!canOpen) {
                e.preventDefault();
                return;
              }
              onProductClick?.(c);
            }}
          >
            {c.src ? (
              <LookTileImage
                src={c.src}
                style={{
                  // Fixed tag square; pan via left/top %, zoom from image center.
                  position: "absolute",
                  left: `${Number(c.x) >= 0 ? Number(c.x) : 50}%`,
                  top: `${Number(c.y) >= 0 ? Number(c.y) : 50}%`,
                  transform:
                    Number(c.zoom) > 1
                      ? `translate(-50%, -50%) scale(${c.zoom})`
                      : "translate(-50%, -50%)",
                  transformOrigin: "center center",
                  objectPosition: "50% 50%",
                }}
              />
            ) : (
              <span className="mte-fc-tag-ph" />
            )}
          </a>
        );
      })}
      {showMoreTile ? (
        more > 0 ? (
          <button
            type="button"
            className="mte-fc-tag is-more"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onMoreClick?.();
            }}
            aria-label={`${more} more products`}
          >
            <span className="mte-fc-tag-more-label">+{more} more</span>
          </button>
        ) : (
          <span className="mte-fc-tag is-more is-ph" aria-hidden>
            <span className="mte-fc-tag-ph" />
          </span>
        )
      ) : null}
    </div>
  );
}

/** Mockup ContentDrawer cell — hairline, contained shot, brand, name. */
function DrawerProductCell({
  product,
  compact,
}: {
  product: ShopCatalogProduct;
  compact?: boolean;
}) {
  const templateAnalytics = useTemplateAnalyticsOptional();
  const shopHref = lookProductHref(product);
  const canOpen = Boolean(shopHref);
  const brand = productBrandLabel(product);
  const name = lookProductLabel(product.name);
  return (
    <a
      href={canOpen ? shopHref : undefined}
      className={`mte-fc-drawer-item${compact ? " is-compact" : ""}`}
      target={canOpen ? "_blank" : undefined}
      rel={canOpen ? "noopener noreferrer" : undefined}
      onClick={(e) => {
        e.stopPropagation();
        if (!canOpen) {
          e.preventDefault();
          return;
        }
        if (product.postId) {
          const productLabel = String(name || product.name || "Shop").trim() || "Shop";
          templateAnalytics?.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.SECTION_CLICK, {
            sectionId: "recentLooks",
            sectionType: "Recent Looks",
            entityType: "section",
            entityId: product.postId,
            entityLabel: productLabel,
            details: {
              name: productLabel,
              image: String(product.image || "").trim(),
              post_id: product.postId,
            },
          });
          templateAnalytics?.trackShopClick({
            postId: product.postId,
            name: name || product.name,
            sectionId: "recentLooks",
            shopView: "content",
            contentCategory: product.contentCategory,
            mediaUrl: product.image,
          });
        }
      }}
    >
      <div className="mte-fc-drawer-rule" />
      <div className="mte-fc-drawer-shot">
        <div className="mte-fc-drawer-frame">
          {product.image ? (
            <LookTileImage src={product.image} />
          ) : (
            <span aria-hidden />
          )}
          {canOpen ? (
            <span className="mte-fc-drawer-shop" aria-hidden>
              Shop
            </span>
          ) : null}
        </div>
      </div>
      {brand ? <div className="mte-fc-drawer-brand">{brand}</div> : null}
      {name || product.name ? (
        <div className="mte-fc-drawer-name">
          <E
            path={`product.${product.postId}.name`}
            className="mte-tpl-prod-name-edit"
          >
            {name || product.name}
          </E>
        </div>
      ) : null}
    </a>
  );
}

function ReelFrame({
  src,
  label,
  hovered,
  zoom = 1,
  x = 50,
  y = 50,
  sizeClass,
  compact,
  shopLabel = "Shop",
  priority = false,
}: {
  src: string;
  label: string;
  hovered?: boolean;
  zoom?: number;
  x?: number;
  y?: number;
  sizeClass?: string;
  /** Trio/Quad — smaller “Shop” chip like the mockup. */
  compact?: boolean;
  shopLabel?: string;
  priority?: boolean;
}) {
  const z = Math.max(1, Number(zoom) || 1);
  const px = Math.min(100, Math.max(0, Number(x) || 50));
  const py = Math.min(100, Math.max(0, Number(y) || 50));
  return (
    <div
      className={`mte-fc-reel-wrap${hovered ? " is-hover" : ""}${
        compact ? " is-compact" : ""
      }${sizeClass ? ` ${sizeClass}` : ""}`}
    >
      <div className="mte-fc-reel" aria-label={label}>
        {src ? (
          <LookTileImage
            src={src}
            priority={priority}
            style={{
              transform: z > 1 ? `scale(${z})` : undefined,
              transformOrigin: `${px}% ${py}%`,
              objectPosition: `${px}% ${py}%`,
            }}
          />
        ) : (
          <span className="mte-fc-reel-ph" />
        )}
      </div>
      <span className="mte-fc-reel-shop" aria-hidden>
        <span>{shopLabel}</span>
        <svg
          width={compact ? 10 : 11}
          height={compact ? 10 : 11}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
      </span>
    </div>
  );
}

function useLookProducts(
  item: CreatorLook,
  username: string,
  userId: string,
  enabled = true,
): { products: ShopCatalogProduct[]; loading: boolean } {
  const isExternal = item.shopKind === "external";
  const shopPosts = useShopLinkIndividualPosts({
    shopUrl: item.post.shopUrl || "",
    postId: item.post.postId,
    username,
    userId,
    enabled:
      enabled &&
      !isExternal &&
      Boolean(item.post.shopUrl || item.post.postId),
  });
  const products =
    shopPosts.products.length > 0
      ? shopPosts.products
      : item.products || [];
  return {
    products,
    // Stay in loading until the first products arrive — avoids skel ↔ empty
    // flashes between shop-url → component → feed handoffs.
    loading: Boolean(!isExternal && shopPosts.loading && products.length === 0),
  };
}

/**
 * Content ProductCard — ports FeaturedContent.jsx ProductCard for reels.
 * Caption removed — hover Shop chip on the reel opens the drawer.
 * beside ≤2 (Hero/Split); Trio/Quad stack tags under the reel group.
 */
function ContentLookCard({
  item,
  slotCount,
  slotIndex,
  isOpen,
  onToggle,
  username,
  userId,
  tagsBeside = "auto",
  titleOverride,
  slotDraft,
  fetchEnabled = true,
}: {
  item: CreatorLook;
  slotCount: number;
  slotIndex: number;
  isOpen: boolean;
  onToggle: () => void;
  username: string;
  userId: string;
  tagsBeside?: LooksTagsBeside;
  titleOverride?: string;
  slotDraft?: LookSlotDraft;
  /** False while the content page layer is CSS-hidden (pre-mounted). */
  fetchEnabled?: boolean;
}) {
  const [hovered, setHovered] = React.useState(false);
  const templateAnalytics = useTemplateAnalyticsOptional();
  const { products, loading: productsLoading } = useLookProducts(
    item,
    username,
    userId,
    fetchEnabled,
  );
  const title =
    String(titleOverride || "").trim() ||
    lookTitle(item.post.caption, "Untitled");
  const reelSrc =
    slotDraft?.contentSource === "upload" && slotDraft.contentSrc
      ? slotDraft.contentSrc
      : item.post.image;
  const reelZoom =
    Number(slotDraft?.contentZoom) > 0 ? Number(slotDraft?.contentZoom) : 1;
  const reelX =
    Number(slotDraft?.contentX) >= 0 ? Number(slotDraft?.contentX) : 50;
  const reelY =
    Number(slotDraft?.contentY) >= 0 ? Number(slotDraft?.contentY) : 50;
  const beside = slotCount <= 2;
  const isHero = slotCount === 1;
  const tagWant = tagWantForLayout(slotCount, tagsBeside);
  const available = countAvailableLookProducts(products, slotDraft?.products);
  const isOurLink =
    item.shopKind === "collection" || item.shopKind === "browsable";
  // Our-link looks: keep tag placeholders up while products resolve so the
  // card footprint doesn’t jump skel ↔ empty ↔ tags.
  // External: only show tags when we actually have product thumbs.
  const hasTagContent =
    tagWant > 0 && (isOurLink ? productsLoading || available > 0 : available > 0);
  // Overflow: last slot is “+N more” for every Products count
  // (2→1 thumb+more, 3→2+more, 4→3+more). While products load, reserve the
  // more-slot so the footprint doesn’t jump when overflow appears.
  const overflow =
    available > tagWant || (productsLoading && available === 0);
  const useMoreFootprint = hasTagContent && tagWant >= 1 && overflow;
  const showMore = hasTagContent && available > tagWant;
  const moreCount = showMore ? available - (tagWant - 1) : 0;
  const tagCells = hasTagContent
    ? resolveTagCells(
        products,
        slotDraft?.products,
        useMoreFootprint ? tagWant - 1 : tagWant,
      )
    : [];
  const shopLabel = isOurLink ? "Shop the look" : "Shop";

  const onActivate = () => {
    const postId = String(item.post.postId || "").trim();
    const lookLabel = String(title || shopLabel || "Open look").trim();
    templateAnalytics?.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.SECTION_CLICK, {
      sectionId: "recentLooks",
      sectionType: "Recent Looks",
      entityType: "section",
      entityId: postId || lookLabel,
      entityLabel: lookLabel,
      details: {
        name: lookLabel,
        image: String(item.post.image || "").trim(),
        post_id: postId,
      },
    });
    if (postId) {
      templateAnalytics?.trackPostClick({
        postId,
        name: title,
        title,
        sectionId: "recentLooks",
        sectionType: "ContentLooks",
        platform: item.post.platform,
        mediaUrl: item.post.image,
        contentView: "latest",
        contentTab: "Looks",
      });
    }
    if (item.shopKind === "external") {
      const url =
        addHttpToLink(String(item.post.shopUrl || "").trim()) ||
        addHttpToLink(String(item.post.permalink || "").trim());
      if (url) {
        if (postId) {
          templateAnalytics?.trackShopClick({
            postId,
            name: title,
            sectionId: "recentLooks",
            shopView: "content",
            platform: item.post.platform,
            mediaUrl: item.post.image,
          });
        }
        openOutboundUrl(url);
      }
      return;
    }
    onToggle();
  };

  const reel = (
    <ReelFrame
      src={reelSrc}
      label={title}
      hovered={hovered || isOpen}
      zoom={reelZoom}
      x={reelX}
      y={reelY}
      compact={slotCount >= 3}
      shopLabel={shopLabel}
      priority={isHero || slotIndex === 0}
    />
  );
  const tags =
    hasTagContent && tagWant > 0 ? (
      <TagBlock
        want={tagWant}
        cells={tagCells}
        moreCount={moreCount}
        reserveMore={useMoreFootprint}
        onMoreClick={onToggle}
        onProductClick={(cell) => {
          const postId = String(cell.postId || "").trim();
          if (!postId) return;
          const productLabel = String(cell.name || "Shop").trim() || "Shop";
          templateAnalytics?.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.SECTION_CLICK, {
            sectionId: "recentLooks",
            sectionType: "Recent Looks",
            entityType: "section",
            entityId: postId,
            entityLabel: productLabel,
            details: {
              name: productLabel,
              image: String(cell.src || "").trim(),
              post_id: postId,
            },
          });
          templateAnalytics?.trackShopClick({
            postId,
            name: cell.name || postId,
            sectionId: "recentLooks",
            shopView: "content",
            mediaUrl: cell.src,
          });
        }}
      />
    ) : null;

  return (
    <div
      className={`mte-fc-card${isOpen ? " is-open" : ""}${
        beside ? " is-beside" : " is-stack"
      }${isHero ? " is-hero" : ""}${
        slotCount === 2 && tagWant === 4 ? " is-split-four" : ""
      }${slotCount === 2 && tagWant === 3 ? " is-split-three" : ""}${
        slotCount === 1 && tagWant === 3 ? " is-hero-three" : ""
      }${tagWant === 3 ? " is-three-tags" : ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onActivate}
      role="button"
      tabIndex={0}
      aria-label={String(title || shopLabel || "Open look").trim()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onActivate();
        }
      }}
      data-look-slot={slotIndex}
    >
      {beside && isHero ? (
        <div className="mte-fc-hero-group">
          {reel}
          {tags}
        </div>
      ) : beside ? (
        <div className="mte-fc-my-spacing is-visual-only">
          <div className="mte-fc-my-left">
            {reel}
            {tags}
          </div>
        </div>
      ) : (
        <div className="mte-fc-stack-group">
          <div className="mte-fc-stack-visual">
            {reel}
            {tags}
          </div>
        </div>
      )}
    </div>
  );
}

/** ContentDrawer — one horizontal scroll row of product cells. */
function ContentLookDrawer({
  products,
  loading,
  isMobile,
  onClose,
}: {
  products: ShopCatalogProduct[];
  loading?: boolean;
  isMobile?: boolean;
  onClose: () => void;
}) {
  return (
    <div className={`mte-fc-drawer${isMobile ? " is-mobile" : ""}`}>
      <div className="mte-fc-drawer-head is-close-only">
        <button
          type="button"
          className="mte-fc-drawer-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </div>
      {loading && !products.length ? (
        <p className="mte-tpl-merch-empty">Loading posts…</p>
      ) : !products.length ? (
        <p className="mte-tpl-merch-empty">No products linked to this look yet.</p>
      ) : (
        <div className="mte-fc-drawer-row is-scroll" role="list">
          {products.map((p, i) => (
            <DrawerProductCell
              key={`${p.postId || "prod"}-${i}`}
              product={p}
              compact
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RecentLooksSectionInner({
  cfg,
  isMobile,
  enabled = true,
  cascadeSectionId = "",
  cascadeAllowsFetch = true,
  pageActive = true,
}: {
  cfg?: SectionProps["cfg"];
  isMobile?: boolean;
  /** False while the content page layer is CSS-hidden (pre-mounted). */
  enabled?: boolean;
  cascadeSectionId?: string;
  cascadeAllowsFetch?: boolean;
  pageActive?: boolean;
}) {
  const CREATOR = useCreatorData();
  const cascade = useSectionNetworkCascade();
  const inEditor = Boolean(useWebsiteEditorInspectorOptional()?.enabled);
  const { customDomain } = useCustomDomain();
  const username = (CREATOR.bio?.username || "").trim().toLowerCase();
  const userId = CREATOR.bio?.userId || "";
  const sectionPath = useTemplateSectionInstance();
  const headingPath = sectionPath
    ? `section.${sectionPath}.heading`
    : "";
  const { get, set } = useEdit();
  // Prefer cfg heading from Add; only migrate the legacy shared path when empty.
  // Seed once per section — do not re-apply after Discard restores contentEdits.
  const seededHeadingPathRef = React.useRef("");
  React.useEffect(() => {
    if (!headingPath) return;
    if (seededHeadingPathRef.current === headingPath) return;
    if (get(headingPath, "\u0000") !== "\u0000") {
      seededHeadingPathRef.current = headingPath;
      return;
    }
    const cfgHeading = (merchCfgString(cfg, "heading") || "")
      .replace(/\s*·\s*Click to shop$/i, "")
      .trim();
    if (cfgHeading) {
      set(headingPath, cfgHeading);
      seededHeadingPathRef.current = headingPath;
      return;
    }
    const legacy = get("looks.recent.heading", "\u0000");
    if (legacy === "\u0000") return;
    set(headingPath, legacy);
    seededHeadingPathRef.current = headingPath;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed once per path
  }, [headingPath]);
  const heading = (merchCfgString(cfg, "heading") || "")
    .replace(/\s*·\s*Click to shop$/i, "")
    .trim();
  const tagsBeside = normalizeLooksTagsBeside(cfg?.tagsBeside);
  const slotCap = normalizeLooksSlotCount(cfg?.count, 1);
  const rawPostIds = React.useMemo(() => {
    const raw = cfg?.postIds;
    if (Array.isArray(raw)) return raw.map((v) => String(v ?? "").trim());
    return merchCfgStringList(cfg, "postIds");
  }, [cfg?.postIds, cfg]);
  const focusPostIds = React.useMemo(() => {
    const ids = rawPostIds.map((id) => String(id || "").trim()).filter(Boolean);
    return ids.length ? ids : null;
  }, [rawPostIds]);
  const { looks, loading, firstPageReady } = useCreatorLooks({
    username,
    userId,
    enabled: Boolean(enabled && username && userId),
    customDomain,
    // Page 1 first; background-warm pages 2–3 after settle (75 look pool).
    maxPages: 3,
    autoWarmPages: true,
    focusPostIds,
  });
  // Gate Content catalog until Recent Looks page 1 can paint.
  useLooksBandCatalogGate(Boolean(enabled && firstPageReady && !loading));

  React.useEffect(() => {
    if (!cascade || !cascadeSectionId || !pageActive || !cascadeAllowsFetch) {
      return;
    }
    if (firstPageReady && !loading) {
      cascade.reportSettled(cascadeSectionId);
    }
  }, [
    cascade,
    cascadeSectionId,
    pageActive,
    cascadeAllowsFetch,
    firstPageReady,
    loading,
  ]);

  const rawTitles = React.useMemo(() => {
    const raw = cfg?.postTitles;
    if (Array.isArray(raw)) return raw.map((v) => String(v ?? "").trim());
    if (typeof raw === "string" && raw.trim()) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed.map((v) => String(v ?? "").trim());
        }
      } catch {
        /* fall through */
      }
      return raw.split(",").map((v) => v.trim());
    }
    return [] as string[];
  }, [cfg?.postTitles]);
  // Preserve pick order; drop blanks. Layout still uses slotCap (Hero→Quad).
  const titleByPostId = React.useMemo(() => {
    const map = new Map<string, string>();
    rawPostIds.forEach((id, i) => {
      const t = String(rawTitles[i] || "").trim();
      if (id && t) map.set(id, t);
    });
    return map;
  }, [rawPostIds, rawTitles]);
  const slotDrafts = React.useMemo(
    () => readLookSlotDrafts(cfg, slotCap),
    [cfg, slotCap],
  );
  const draftByPostId = React.useMemo(() => {
    const map = new Map<string, LookSlotDraft>();
    rawPostIds.forEach((id, i) => {
      if (id) map.set(id, slotDrafts[i] || {});
    });
    return map;
  }, [rawPostIds, slotDrafts]);
  const featuredId = merchCfgString(cfg, "excludePostId");
  const byId = React.useMemo(() => {
    const map = new Map<string, CreatorLook>();
    for (const item of looks) map.set(item.post.postId, item);
    return map;
  }, [looks]);

  // Layout follows How many (Hero→Quad), not how many posts are filled yet.
  const slots = React.useMemo(() => {
    return Array.from({ length: slotCap }, (_, i) => {
      const id = String(rawPostIds[i] || "").trim();
      const item =
        id && id !== featuredId ? byId.get(id) || null : null;
      return { index: i, id, item };
    });
  }, [slotCap, rawPostIds, byId, featuredId]);

  // Track open drawer by slot index — the same post can fill multiple slots,
  // so postId is not a unique React key / open identity.
  const [openSlot, setOpenSlot] = React.useState<number | null>(null);
  const [mobilePage, setMobilePage] = React.useState(0);
  /** 1 or 2 looks per page — measured from the band width. */
  const [mobilePerPage, setMobilePerPage] = React.useState<1 | 2>(2);
  const bandWrapRef = React.useRef<HTMLDivElement | null>(null);
  const bandScrollRef = React.useRef<HTMLDivElement | null>(null);
  const open =
    openSlot != null
      ? slots.find((s) => s.index === openSlot)?.item || null
      : null;

  // Opening a look reveals its products in place. Bring that look to the top
  // of the page so the data doesn't stay at the click position.
  React.useLayoutEffect(() => {
    if (openSlot == null) return;
    const card = bandWrapRef.current?.querySelector(
      `[data-look-slot="${openSlot}"]`,
    );
    if (!(card instanceof HTMLElement)) return;
    scrollElementToFeedTop(card);
  }, [openSlot]);

  React.useEffect(() => {
    if (openSlot == null) return;
    const slot = slots.find((s) => s.index === openSlot);
    if (!slot?.item) setOpenSlot(null);
  }, [openSlot, slots]);

  // Trio/Quad on mobile: pick 1-up vs 2-up from available width (not a fixed 2).
  React.useEffect(() => {
    if (!isMobile || slotCap <= 2) {
      setMobilePerPage(2);
      return;
    }
    const el = bandWrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      setMobilePerPage(1);
      return;
    }
    let raf = 0;
    const measure = () => {
      const w = el.clientWidth;
      // Two looks (reel + product stack) need ~200px each + gap.
      // Below that, fall back to a single look so chips/tags aren’t crushed.
      const next: 1 | 2 = w >= 420 ? 2 : 1;
      setMobilePerPage((prev) => (prev === next ? prev : next));
    };
    // Defer out of the ResizeObserver delivery frame — sync setState/layout
    // here surfaces as “ResizeObserver loop completed with undelivered notifications”.
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    schedule();
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [isMobile, slotCap]);

  const openShopPosts = useShopLinkIndividualPosts({
    shopUrl: open?.post.shopUrl || "",
    postId: open?.post.postId,
    username,
    userId,
    enabled: Boolean(
      enabled && open && open.shopKind !== "external",
    ),
  });
  const openProducts =
    openShopPosts.products.length > 0
      ? openShopPosts.products
      : open?.products || [];

  // Public visitors never see blank "Content N" shells — only filled looks
  // (plus loading placeholders for configured ids still resolving).
  const visibleSlots = React.useMemo(() => {
    if (inEditor) return slots;
    return slots.filter((s) => Boolean(s.item) || Boolean(s.id && loading));
  }, [inEditor, slots, loading]);

  const useMobilePager = Boolean(isMobile && visibleSlots.length > 2);
  const mobilePageCount = Math.max(
    1,
    Math.ceil(
      visibleSlots.length /
        (useMobilePager ? mobilePerPage : visibleSlots.length || 1),
    ),
  );
  const pagedSlotGroups = React.useMemo(() => {
    if (!useMobilePager) return null;
    const groups: (typeof visibleSlots)[] = [];
    for (let i = 0; i < visibleSlots.length; i += mobilePerPage) {
      groups.push(visibleSlots.slice(i, i + mobilePerPage));
    }
    return groups;
  }, [mobilePerPage, visibleSlots, useMobilePager]);

  const scrollBandToPage = React.useCallback(
    (page: number, smooth = true) => {
      const el = bandScrollRef.current;
      const next = Math.max(0, Math.min(mobilePageCount - 1, page));
      setMobilePage(next);
      if (!el) return;
      const pageEl = el.querySelectorAll(".mte-fc-band-page")[next];
      const left =
        pageEl instanceof HTMLElement ? pageEl.offsetLeft : next * el.clientWidth;
      el.scrollTo({
        left,
        behavior: smooth ? "smooth" : "auto",
      });
    },
    [mobilePageCount],
  );

  React.useEffect(() => {
    setMobilePage((p) => Math.min(p, mobilePageCount - 1));
  }, [mobilePageCount]);
  React.useEffect(() => {
    setMobilePage(0);
    const el = bandScrollRef.current;
    if (el) el.scrollTo({ left: 0, behavior: "auto" });
  }, [mobilePerPage]);

  React.useEffect(() => {
    const el = bandScrollRef.current;
    if (!el || !useMobilePager) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const pages = el.querySelectorAll(".mte-fc-band-page");
        let best = 0;
        let bestDist = Infinity;
        pages.forEach((page, i) => {
          if (!(page instanceof HTMLElement)) return;
          const dist = Math.abs(page.offsetLeft - el.scrollLeft);
          if (dist < bestDist) {
            bestDist = dist;
            best = i;
          }
        });
        setMobilePage((p) => {
          const next = Math.max(0, Math.min(mobilePageCount - 1, best));
          return p === next ? p : next;
        });
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", onScroll);
    };
  }, [mobilePageCount, useMobilePager]);

  // Mobile pager: if the drawer is open and the user changes slides, rebind to
  // a look on the new page so products update (don’t keep the previous look).
  React.useEffect(() => {
    if (!useMobilePager || openSlot == null) return;
    const pageStart = mobilePage * mobilePerPage;
    const pageSlots = visibleSlots.slice(pageStart, pageStart + mobilePerPage);
    const stillVisible = pageSlots.some((s) => s.index === openSlot && s.item);
    if (stillVisible) return;
    const next = pageSlots.find((s) => s.item);
    setOpenSlot(next ? next.index : null);
  }, [mobilePage, mobilePerPage, openSlot, visibleSlots, useMobilePager]);

  const filledCount = slots.filter((s) => s.item).length;
  // Editor keeps the configured Hero→Quad shell; public sizes to what painted.
  const count = inEditor
    ? slotCap
    : Math.max(1, Math.min(4, visibleSlots.length || filledCount || 1));
  const isHero = count === 1;
  const evenly = count >= 3;
  const bandCountClass =
    count === 1
      ? "is-count-1"
      : count === 2
        ? "is-count-2"
        : count === 3
          ? "is-count-3"
          : "is-count-4";

  const showMobilePager = Boolean(useMobilePager && mobilePageCount > 1);

  const renderSlot = (slot: (typeof slots)[number]) => {
    const look = slot.item;
    if (!look) {
      // Configured slot still resolving looks list — keep placeholder until
      // look pages finish warming (don’t flash “Content N” between pages).
      // Public already filtered these out unless still loading.
      const waitingOnLook = Boolean(slot.id && loading);
      if (!inEditor && !waitingOnLook) return null;
      return (
        <div
          key={`empty-${slot.index}`}
          className={`mte-fc-card is-empty${
            count <= 2 ? " is-beside" : " is-stack"
          }${isHero ? " is-hero" : ""}${
            waitingOnLook ? " is-skel" : ""
          }`}
          aria-label={
            waitingOnLook
              ? `Loading look ${slot.index + 1}`
              : `Content ${slot.index + 1} empty`
          }
          aria-busy={waitingOnLook || undefined}
        >
          <div className="mte-fc-empty">
            <span
              className={`mte-fc-empty-reel${
                waitingOnLook ? " is-ph" : ""
              }`}
              aria-hidden
            />
            {waitingOnLook || !inEditor ? null : (
              <span className="mte-fc-empty-label">
                Content {slot.index + 1}
              </span>
            )}
          </div>
        </div>
      );
    }
    return (
      <ContentLookCard
        key={`look-slot-${slot.index}`}
        item={look}
        slotCount={count}
        slotIndex={slot.index}
        isOpen={openSlot === slot.index}
        onToggle={() =>
          setOpenSlot((prev) => (prev === slot.index ? null : slot.index))
        }
        username={username}
        userId={userId}
        tagsBeside={tagsBeside}
        titleOverride={
          String(rawTitles[slot.index] || "").trim() ||
          titleByPostId.get(look.post.postId)
        }
        slotDraft={
          slotDrafts[slot.index] || draftByPostId.get(look.post.postId)
        }
        fetchEnabled={enabled}
      />
    );
  };

  return (
    <div className="mte-tpl-looks">
      <p className="mte-tpl-looks-kicker">
        {headingPath ? (
          <E path={headingPath} placeholder="Enter a title">
            {heading}
          </E>
        ) : (
          heading
        )}
      </p>
      {!filledCount && !loading ? (
        <p className="mte-tpl-merch-empty">
          Choose posts in the editor to show recent looks here.
        </p>
      ) : null}
      <div
        ref={bandWrapRef}
        className={`mte-fc-band-wrap${showMobilePager ? " has-pager" : ""}`}
      >
        <div
          ref={bandScrollRef}
          className={`mte-fc-band ${bandCountClass}${evenly ? " is-evenly" : ""}${
            isMobile ? " is-mobile" : ""
          }${isHero ? " is-hero-band" : ""}${
            showMobilePager ? " is-mobile-paged" : ""
          }${
            showMobilePager
              ? mobilePerPage === 1
                ? " is-per-1"
                : " is-per-2"
              : ""
          }`}
        >
          {showMobilePager && pagedSlotGroups
            ? pagedSlotGroups.map((pageSlots, pageIndex) => (
                <div
                  key={`looks-page-${pageIndex}`}
                  className="mte-fc-band-page"
                >
                  {pageSlots.map(renderSlot)}
                </div>
              ))
            : visibleSlots.map(renderSlot)}
        </div>
        {showMobilePager ? (
          <div className="mte-fc-band-pager" role="group" aria-label="Looks pages">
            <button
              type="button"
              className="mte-fc-band-pager-btn"
              disabled={mobilePage <= 0}
              onClick={() => scrollBandToPage(mobilePage - 1)}
              aria-label="Previous looks"
            >
              ←
            </button>
            <span className="mte-fc-band-pager-dots" aria-hidden>
              {Array.from({ length: mobilePageCount }, (_, i) => (
                <span
                  key={i}
                  className={`mte-fc-band-pager-dot${
                    i === mobilePage ? " is-on" : ""
                  }`}
                />
              ))}
            </span>
            <button
              type="button"
              className="mte-fc-band-pager-btn"
              disabled={mobilePage >= mobilePageCount - 1}
              onClick={() => scrollBandToPage(mobilePage + 1)}
              aria-label="Next looks"
            >
              →
            </button>
          </div>
        ) : null}
      </div>
      {open && open.shopKind !== "external" ? (
        <ContentLookDrawer
          products={openProducts}
          loading={openShopPosts.loading}
          isMobile={isMobile}
          onClose={() => setOpenSlot(null)}
        />
      ) : null}
    </div>
  );
}

/** Recent Looks — Chandler Featured Content band + ContentDrawer. */
export function RecentLooksSection({
  T,
  cfg,
  isActive = true,
}: SectionProps) {
  const sectionPath = useTemplateSectionInstance();
  const cascade = useSectionNetworkCascade();
  const cascadeSectionId =
    String(sectionPath || "")
      .split("/")
      .pop()
      ?.trim() || "";
  const cascadeAllowsFetch =
    !cascade || !cascadeSectionId || cascade.canFetch(cascadeSectionId);
  const looksEnabled = Boolean(isActive && cascadeAllowsFetch);

  const isMobile = T.device === "mobile";
  const shellCfg = {
    ...(cfg || {}),
    heading: undefined,
    bgTone: (cfg?.bgTone as "bg" | "surface" | "accent" | undefined) || "bg",
  };
  return (
    <Shell
      T={T}
      cfg={shellCfg}
      fullBleed
      independentShellClassName="mte-tpl-looks-shell"
    >
      <RecentLooksSectionInner
        cfg={cfg}
        isMobile={isMobile}
        enabled={looksEnabled}
        cascadeSectionId={cascadeSectionId}
        cascadeAllowsFetch={cascadeAllowsFetch}
        pageActive={isActive}
      />
    </Shell>
  );
}
RecentLooksSection.options = {
  heading: {
    kind: "text",
    label: "Title",
    default: "",
    placeholder: "Enter a title",
  },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface"],
    default: "bg",
  },
} as SectionOptions;
